import { useState, type ReactElement, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { getErrorCodeForUi } from '@/api/errors';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

import type { CreateTemplateInput } from '../api';

interface FieldErrors {
  name?: string;
  definition?: string;
}

interface DraftState {
  name: string;
  description: string;
  definition: string;
}

const EMPTY_DRAFT: DraftState = { name: '', description: '', definition: '' };

export function CreateTemplateDialog({
  open,
  onOpenChange,
  onSubmit,
  pending,
  submitError,
}: {
  readonly open: boolean;
  readonly onOpenChange: (next: boolean) => void;
  readonly onSubmit: (input: CreateTemplateInput) => void;
  readonly pending: boolean;
  readonly submitError: unknown;
}): ReactElement {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT);
  const [errors, setErrors] = useState<FieldErrors>({});

  function handleClose(next: boolean): void {
    if (!next) {
      setDraft(EMPTY_DRAFT);
      setErrors({});
    }
    onOpenChange(next);
  }

  function handleSubmit(event: SyntheticEvent): void {
    event.preventDefault();
    const next: FieldErrors = {};
    const name = draft.name.trim();
    const description = draft.description.trim();
    const definitionText = draft.definition.trim();
    if (name.length === 0) {
      next.name = t('routes.platform.templates.create.errors.nameRequired');
    }
    let definition: unknown;
    if (definitionText.length > 0) {
      try {
        definition = JSON.parse(definitionText);
      } catch {
        next.definition = t('routes.platform.templates.create.errors.definitionInvalid');
      }
    }
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    setErrors({});
    onSubmit({
      name,
      ...(description.length > 0 ? { description } : {}),
      ...(definitionText.length > 0 ? { definition } : {}),
    });
  }

  const submitErrorCode = getErrorCodeForUi(submitError);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent disableOutsideClose>
        <DialogHeader>
          <DialogTitle>{t('routes.platform.templates.create.title')}</DialogTitle>
          <DialogDescription>
            {t('routes.platform.templates.create.description')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <FormField
            id="create-template-name"
            label={t('routes.platform.templates.create.fields.name')}
            required
            error={errors.name}
          >
            {(fp) => (
              <Input
                {...fp}
                value={draft.name}
                onChange={(e) => { setDraft((p) => ({ ...p, name: e.target.value })); }}
                autoComplete="off"
              />
            )}
          </FormField>
          <FormField
            id="create-template-description"
            label={t('routes.platform.templates.create.fields.description')}
          >
            {(fp) => (
              <Input
                {...fp}
                value={draft.description}
                onChange={(e) => { setDraft((p) => ({ ...p, description: e.target.value })); }}
                autoComplete="off"
              />
            )}
          </FormField>
          <FormField
            id="create-template-definition"
            label={t('routes.platform.templates.create.fields.definition')}
            error={errors.definition}
          >
            {(fp) => (
              <Textarea
                {...fp}
                value={draft.definition}
                onChange={(e) => { setDraft((p) => ({ ...p, definition: e.target.value })); }}
                rows={6}
                spellCheck={false}
                className={`${fp.className} font-mono text-[12px]`}
              />
            )}
          </FormField>
          {submitError !== undefined && submitError !== null && (
            <p role="alert" className="text-sm text-red-600">
              {t('routes.platform.templates.create.errors.submit', { code: submitErrorCode })}
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => { handleClose(false); }}
              disabled={pending}
            >
              {t('routes.platform.templates.create.cancel')}
            </Button>
            <Button type="submit" loading={pending}>
              {t('routes.platform.templates.create.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
